import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { Button, PageHeader, Skeleton, Tabs } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/axios';
import { type CorbeilleEtat, listCorbeille, purgerItem, restaurerItem } from '@/services/corbeille';
import { toast } from '@/store/toastStore';

export function CorbeillePage() {
  const queryClient = useQueryClient();
  const [etat, setEtat] = useState<CorbeilleEtat>('archive');
  const { data, isLoading } = useQuery({ queryKey: ['corbeille', etat], queryFn: () => listCorbeille(etat) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['corbeille'] });

  const restoreM = useMutation({
    mutationFn: (v: { entite: string; id: string }) => restaurerItem(v.entite, v.id, etat),
    onSuccess: () => { invalidate(); toast.success('Élément restauré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const purgeM = useMutation({
    mutationFn: (v: { entite: string; id: string }) => purgerItem(v.entite, v.id),
    onSuccess: () => { invalidate(); toast.success('Supprimé définitivement.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const groupes = data ?? [];
  const total = groupes.reduce((s, g) => s + g.items.length, 0);

  return (
    <>
      <PageHeader title="Archives & Corbeille" subtitle="Restaurer les éléments archivés ou supprimés, ou les effacer définitivement" breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Archives & Corbeille' }]} />

      <Tabs
        tabs={[
          { key: 'archive', label: 'Archives', icon: <Archive className="size-4" /> },
          { key: 'supprime', label: 'Corbeille (supprimés)', icon: <Trash2 className="size-4" /> },
        ]}
        value={etat}
        onChange={(k) => setEtat(k as CorbeilleEtat)}
        className="mb-6"
      />

      {etat === 'supprime' && (
        <p className="mb-4 rounded-md border border-status-stuck/30 bg-status-stuck/5 px-4 py-2 text-sm text-status-stuck">
          ⚠️ La suppression définitive est <strong>irréversible</strong>. Restaurez plutôt si un doute subsiste.
        </p>
      )}

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : groupes.length === 0 ? (
        <p className="py-16 text-center text-sm text-content-muted">{etat === 'archive' ? 'Aucun élément archivé.' : 'La corbeille est vide.'}</p>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-content-muted">{total} élément(s) dans {groupes.length} catégorie(s).</p>
          {groupes.map((g) => (
            <div key={g.entite} className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
              <div className="flex items-center justify-between border-b border-border bg-surface-primary/40 px-4 py-2.5">
                <h3 className="text-sm font-semibold text-content-primary">{g.label}</h3>
                <span className="rounded-full bg-brand-primary/10 px-2 py-0.5 text-xs font-medium text-brand-primary">{g.items.length}</span>
              </div>
              <ul className="divide-y divide-border">
                {g.items.map((it) => (
                  <li key={it.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="truncate text-sm text-content-secondary">{it.libelle}</span>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button variant="secondary" size="sm" onClick={() => restoreM.mutate({ entite: g.entite, id: it.id })} disabled={restoreM.isPending}>
                        <ArchiveRestore className="size-4" /> Restaurer
                      </Button>
                      {etat === 'supprime' && (
                        <Button variant="ghost" size="sm" className="text-status-stuck" onClick={() => { if (window.confirm('Supprimer DÉFINITIVEMENT cet élément ? Cette action est irréversible.')) purgeM.mutate({ entite: g.entite, id: it.id }); }} disabled={purgeM.isPending}>
                          <Trash2 className="size-4" /> Supprimer définitivement
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
